import { of, from, merge } from "rxjs";
import { switchMap } from "rxjs/operators";
import { ofType } from "redux-observable";
import { sessionLoad, sessionError } from "@dux/session";
import {
  activeLinkDeleted,
  buildNavigationEdit,
  buildNavigationSaveParams,
} from "@domain/sitemap";

export const SITEMAP_UPDATE = "pss/sitemap/UPDATE";
export const SITEMAP_START_LINK_EDIT = "pss/sitemap/START_LINK_EDIT";
export const SITEMAP_START_LINK_EDIT_ERROR =
  "pss/sitemap/START_LINK_EDIT_ERROR";
export const SITEMAP_START_LINK_EDIT_SUCCESS =
  "pss/sitemap/START_LINK_EDIT_SUCCESS";
export const SITEMAP_PREVIEW_LINK_EDIT = "pss/sitemap/PREVIEW_LINK_EDIT";
export const SITEMAP_PREVIEW_LINK_EDIT_ERROR =
  "pss/sitemap/PREVIEW_LINK_EDIT_ERROR";
export const SITEMAP_PREVIEW_LINK_EDIT_SUCCESS =
  "pss/sitemap/PREVIEW_LINK_EDIT_SUCCESS";
export const SITEMAP_DISCARD_LINK_EDIT = "pss/sitemap/DISCARD_LINK_EDIT";
export const SITEMAP_UPDATE_LINK_EDIT = "pss/sitemap/UPDATE_LINK_EDIT";
export const SITEMAP_SAVE_LINK_EDIT = "pss/sitemap/SAVE_LINK_EDIT";
export const SITEMAP_START_GLOBAL_EDIT = "pss/sitemap/START_GLOBAL_EDIT";
export const SITEMAP_UPDATE_GLOBAL_EDIT = "pss/sitemap/UPDATE_GLOBAL_EDIT";
export const SITEMAP_DISCARD_GLOBAL_EDIT = "pss/sitemap/DISCARD_GLOBAL_EDIT";
export const SITEMAP_SAVE_GLOBAL_EDIT = "pss/sitemap/SAVE_GLOBAL_EDIT";

const initialLinkEdit = {
  editLoading: true,
  editLoadError: null,
  editData: null,
  previewLoading: false,
  previewLoadError: null,
  previewData: null,
};

const initialState = {
  sessionData: null,
  activeLinkEdits: {},
  globalEdit: null,
  editingDomainId: null,
  globalEditDirty: false,
  editingLinkId: null,
  activeLinkDeleted: false,
};

export default function reducer(state = initialState, action) {
  switch (action.type) {
    case SITEMAP_UPDATE:
      return {
        ...state,
        sessionData: action.payload,
      };
    case SITEMAP_START_LINK_EDIT:
      return {
        ...state,
        activeLinkEdits: {
          ...state.activeLinkEdits,
          [action.payload]: initialLinkEdit,
        },
        editingLinkId: action.payload,
      };
    case SITEMAP_START_LINK_EDIT_ERROR:
      return {
        ...state,
        activeLinkEdits: {
          ...state.activeLinkEdits,
          [action.payload.linkId]: {
            ...state.activeLinkEdits[action.payload.linkId],
            editLoadError: action.payload.error,
            editLoading: false,
          },
        },
      };
    case SITEMAP_START_LINK_EDIT_SUCCESS:
      return {
        ...state,
        activeLinkEdits: {
          ...state.activeLinkEdits,
          [action.payload.linkId]: {
            ...state.activeLinkEdits[action.payload.linkId],
            editData: action.payload.data,
            editLoading: false,
          },
        },
      };
    case SITEMAP_PREVIEW_LINK_EDIT:
      if (!state.activeLinkEdits[action.payload]) return state;
      return {
        ...state,
        activeLinkEdits: {
          ...state.activeLinkEdits,
          [action.payload]: {
            ...state.activeLinkEdits[action.payload],
            previewLoading: true,
            previewLoadError: null,
            previewData: null,
          },
        },
      };
    case SITEMAP_PREVIEW_LINK_EDIT_ERROR:
      return {
        ...state,
        activeLinkEdits: {
          ...state.activeLinkEdits,
          [action.payload.linkId]: {
            ...state.activeLinkEdits[action.payload.linkId],
            previewLoadError: action.payload.error,
            previewLoading: false,
          },
        },
      };
    case SITEMAP_PREVIEW_LINK_EDIT_SUCCESS:
      return {
        ...state,
        activeLinkEdits: {
          ...state.activeLinkEdits,
          [action.payload.linkId]: {
            ...state.activeLinkEdits[action.payload.linkId],
            previewData: action.payload.data,
            previewLoading: false,
          },
        },
      };
    case SITEMAP_UPDATE_LINK_EDIT:
      return {
        ...state,
        activeLinkEdits: {
          ...state.activeLinkEdits,
          [action.payload.linkId]: {
            ...state.activeLinkEdits[action.payload.linkId],
            editData: {
              ...state.activeLinkEdits[action.payload.linkId].editData,
              ...action.payload.data,
            },
          },
        },
      };
    case SITEMAP_DISCARD_LINK_EDIT:
      let activeLinkEdits = { ...state.activeLinkEdits };
      delete activeLinkEdits[action.payload];
      return { ...state, activeLinkEdits };
    case SITEMAP_START_GLOBAL_EDIT:
      return {
        ...state,
        editingDomainId: action.payload,
        globalEdit: buildNavigationEdit(state.sessionData, action.payload),
      };
    case SITEMAP_UPDATE_GLOBAL_EDIT:
      const globalEdit = { ...state.globalEdit, ...action.payload };
      return {
        ...state,
        globalEdit,
        globalEditDirty: true,
        activeLinkDeleted: activeLinkDeleted(
          state.sessionData,
          globalEdit,
          state.editingLinkId,
          state.editingDomainId
        ),
      };
    case SITEMAP_DISCARD_GLOBAL_EDIT:
      return {
        ...state,
        globalEdit: null,
        editingDomainId: null,
        globalEditDirty: false,
        activeLinkDeleted: false,
      };
    default:
      return state;
  }
}

export function sitemapUpdate(sitemap) {
  return { type: SITEMAP_UPDATE, payload: sitemap };
}

export function sitemapStartLinkEdit(linkId) {
  return { type: SITEMAP_START_LINK_EDIT, payload: linkId };
}

function sitemapStartLinkEditError(linkId, error) {
  return { type: SITEMAP_START_LINK_EDIT_ERROR, payload: { linkId, error } };
}

function sitemapStartLinkEditSuccess(linkId, data) {
  return { type: SITEMAP_START_LINK_EDIT_SUCCESS, payload: { linkId, data } };
}

export function sitemapPreviewLinkEdit(linkId) {
  return { type: SITEMAP_PREVIEW_LINK_EDIT, payload: linkId };
}

// function sitemapPreviewLinkEditError(linkId, error) {
//   return { type: SITEMAP_PREVIEW_LINK_EDIT_ERROR, payload: { linkId, error } };
// }

// function sitemapPreviewLinkEditSuccess(linkId, data) {
//   return { type: SITEMAP_PREVIEW_LINK_EDIT_SUCCESS, payload: { linkId, data } };
// }

export function sitemapDiscardLinkEdit(linkId) {
  return { type: SITEMAP_DISCARD_LINK_EDIT, payload: linkId };
}

export function sitemapUpdateLinkEdit(linkId, data) {
  return { type: SITEMAP_UPDATE_LINK_EDIT, payload: { linkId, data } };
}

export function sitemapSaveLinkEdit(linkId) {
  return { type: SITEMAP_SAVE_LINK_EDIT, payload: linkId };
}

export function sitemapStartGlobalEdit(activeDomainId) {
  return { type: SITEMAP_START_GLOBAL_EDIT, payload: activeDomainId };
}

export function sitemapUpdateGlobalEdit(data) {
  return { type: SITEMAP_UPDATE_GLOBAL_EDIT, payload: data };
}

export function sitemapDiscardGlobalEdit() {
  return { type: SITEMAP_DISCARD_GLOBAL_EDIT };
}

export function sitemapSaveGlobalEdit() {
  return { type: SITEMAP_SAVE_GLOBAL_EDIT };
}

export function startLinkEditEpic(action$, store$, { api }) {
  return action$.pipe(
    ofType(SITEMAP_START_LINK_EDIT),
    switchMap(({ payload: linkId }) => {
      return from(api.safeFetchPost("/Sitemap/Edit", { linkId })).pipe(
        switchMap((result) => {
          if (result.error)
            return of(sitemapStartLinkEditError(linkId, result.error));
          return of(
            sitemapStartLinkEditSuccess(linkId, {
              link: JSON.stringify(
                { ...result.data.link, links: undefined },
                null,
                2
              ),
              sublinks: result.data.link.links || [],
              references: JSON.stringify(result.data.references, null, 2),
              images: result.data.images,
              settings: JSON.stringify(result.data.settings, null, 2),
              bindings: result.data.bindings || [],
            })
          );
        })
      );
    })
  );
}

function makeSaveGlobalEdit(store, api) {
  return () => {
    const { sessionData, editingDomainId, globalEdit, globalEditDirty } =
      store.sitemap;

    if (!globalEditDirty) return of(sitemapDiscardGlobalEdit());

    return from(
      api.safeFetchPut(
        "/Sitemap/Navigation",
        buildNavigationSaveParams(sessionData, editingDomainId, globalEdit)
      )
    ).pipe(
      switchMap((result) => {
        if (result.error) return of(sessionError(result.error));
        return of(sitemapDiscardGlobalEdit());
      })
    );
  };
}

function makeSaveLinkEdit(linkId, store, api) {
  return () => {
    const { currentRole: role, currentCulture: culture } = store.session;

    if (store.sitemap.activeLinkDeleted)
      return of(sessionLoad({ role, culture }));

    const { link, sublinks, references, images, settings, bindings } =
      store.sitemap.activeLinkEdits[linkId].editData;

    return from(
      api.safeFetchPut("/Sitemap/Edit", {
        linkId,
        link: { ...JSON.parse(link), links: sublinks },
        references: JSON.parse(references),
        settings: JSON.parse(settings),
        images,
        bindings,
      })
    ).pipe(
      switchMap((result) => {
        if (result.error) return of(sessionError(result.error));
        return of(
          sitemapDiscardLinkEdit(linkId),
          sessionLoad({ role, culture })
        );
      })
    );
  };
}

function injectForAction(creatorMap) {
  return switchMap((action) => {
    const creator = creatorMap[action?.type];
    if (creator) return merge(of(action), creator());
    return of(action);
  });
}

export function saveLinkEditEpic(action$, store$, { api }) {
  return action$.pipe(
    ofType(SITEMAP_SAVE_LINK_EDIT),
    switchMap(({ payload }) =>
      makeSaveGlobalEdit(store$.value, api)().pipe(
        injectForAction({
          [SITEMAP_DISCARD_GLOBAL_EDIT]: makeSaveLinkEdit(
            payload,
            store$.value,
            api
          ),
        })
      )
    )
  );
}

export const sitemapEpics = [startLinkEditEpic, saveLinkEditEpic];
